import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { COLORS } from '@mafc/common';
import { HintIcon } from '@mafc/icon';
import { Box, Image, Input, LinearProgress } from '@mafc/trolly';

import SaveMoney from 'assets/save-money.png';
import { AppliedVoucher } from 'network/response/ICart';
import { ICheckoutState } from 'pages/Checkout/redux/reducer';
import { getDiscountAmount } from '../Promo/Promo.helper';
import {
  AppliedPromoDescription,
  ErrorMessage,
  HintIconWrapper,
  PromoCodeDrawer,
} from './PromoCodeDrawer.style';
import { createMarkup } from 'utils/html.util';
import { resetPromo } from 'pages/Checkout/redux/actions/promo.action';

const PromoCodeDrawerBody: React.FC<{
  appliedPromo: AppliedVoucher | null;
  promoCode: string;
  onPromoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onApply: () => void;
}> = ({ appliedPromo, promoCode, onPromoChange, onApply }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { error, loading } = useSelector(
    (state: { checkout: ICheckoutState }) => state.checkout.promo
  );

  const [errorMsg, setErrorMsg] = useState(error);

  const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMsg('');
    onPromoChange(e);
  };

  useEffect(() => {
    setErrorMsg(error);
  }, [error]);

  const submitHandler = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (promoCode) {
      onApply();
    }
  };

  const resetError = () => {
    dispatch(resetPromo());
  };

  return (
    <PromoCodeDrawer>
      {appliedPromo ? (
        <>
          <Image mb={4} url={SaveMoney} name="save-money" height="250px" width="auto" />
          <AppliedPromoDescription>
            <Box mb={3}>{t('promo.successMessage1')}</Box>
            <Box
              dangerouslySetInnerHTML={createMarkup(
                t('promo.successMessage2', {
                  discount: getDiscountAmount(appliedPromo),
                })
              )}
            />
          </AppliedPromoDescription>
        </>
      ) : (
        <>
          <form onSubmit={submitHandler} name="promo-code">
            <Input
              label={`${t('promo.voucherCode')}`}
              value={promoCode}
              onChange={changeHandler}
              disabled={loading ? true : false}
              variant={errorMsg ? 'error' : 'primary'}
              mui
              data-testid="voucher-code-text"
              showClearErrorIcon
              onClearErrorIconClick={resetError}
            />

            {errorMsg && (
              <ErrorMessage>
                <HintIconWrapper>
                  <HintIcon fill={COLORS.DARK_RED} height={20} width={20} size="CUSTOM" />
                </HintIconWrapper>

                {errorMsg}
              </ErrorMessage>
            )}
          </form>

          {loading && <LinearProgress position="bottom" />}
        </>
      )}
    </PromoCodeDrawer>
  );
};

export default PromoCodeDrawerBody;
